import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { ExportButton } from '@/app/ExportButton';
import { formatMoney, formatDate } from '@/lib/money';
import type { MoneyString } from '@/lib/money';

type Row = {
  id: number | null;
  label: string;
  total: MoneyString;
  open: MoneyString;
  count: number;
  percent: number;
  previous: MoneyString;
  delta: MoneyString;
  /** Nulo quando não havia base — crescer de zero não é 100%. */
  delta_percent: number | null;
};

type Breakdown = {
  dimension: string;
  dimension_title: string;
  type: 'receita' | 'despesa';
  period: { from: string; to: string };
  previous_period: { from: string; to: string };
  rows: Row[];
  total: MoneyString;
  previous_total: MoneyString;
  delta_percent: number | null;
};

const DIMENSOES: Array<{ id: string; label: string; hint: string }> = [
  { id: 'category', label: 'Categoria', hint: 'Onde o dinheiro vai' },
  { id: 'party', label: 'Cliente / Fornecedor', hint: 'Quem paga e quem recebe' },
  { id: 'contract', label: 'Contrato', hint: 'Quanto cada acordo rende ou custa' },
  { id: 'card', label: 'Cartão', hint: 'Quanto passa em cada cartão' },
  { id: 'entity', label: 'Entidade', hint: 'PJ, PF e o que é de cada uma' },
  { id: 'cost_center', label: 'Centro de custo', hint: 'Por área ou projeto' },
];

/**
 * Análise por dimensão — RF-REL-04, RF-REL-05.
 *
 * Um componente para seis relatórios. A pergunta é sempre a mesma — quanto
 * entrou ou saiu, agrupado por X — e só muda o agrupamento.
 *
 * Toda linha traz o período anterior ao lado. Um valor sozinho não responde
 * nada: "Mercado R$ 1.240" só vira informação ao lado de "no período anterior
 * foi R$ 890".
 */
export function BreakdownReport({
  range,
  selectedIds,
}: {
  range: { from: string; to: string };
  selectedIds: number[];
}) {
  const [dimension, setDimension] = useState('category');
  const [type, setType] = useState<'receita' | 'despesa'>('despesa');

  const query = useQuery({
    queryKey: ['report-breakdown', dimension, type, range.from, range.to, selectedIds],
    queryFn: () =>
      api.get<{ data: Breakdown }>(
        `/reports/breakdown?dimension=${dimension}&type=${type}&from=${range.from}&to=${range.to}`,
        { entityIds: selectedIds },
      ),
    select: (r) => r.data,
  });

  const info = DIMENSOES.find((d) => d.id === dimension);
  const d = query.data;

  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-end gap-2.5">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
            Agrupar por
          </span>
          <select
            value={dimension}
            onChange={(e) => setDimension(e.target.value)}
            className="input"
          >
            {DIMENSOES.map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
        </label>

        <div className="mb-0 ml-auto">
          <ExportButton
            path="/reports/breakdown/export"
            query={{ dimension, type, from: range.from, to: range.to }}
            entityIds={selectedIds}
            disabled={query.isPending}
          />
        </div>

        <div className="mb-0 flex rounded-lg border border-slate-300 p-0.5 dark:border-slate-700">
          {([['despesa', 'Despesas'], ['receita', 'Receitas']] as const).map(([id, label]) => (
            <button
              key={id}
              onClick={() => setType(id)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
                type === id
                  ? id === 'despesa'
                    ? 'bg-rose-600 text-white'
                    : 'bg-emerald-600 text-white'
                  : 'text-slate-600 hover:text-slate-900 dark:text-slate-400'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {info && <span className="mb-2 text-xs text-slate-400">{info.hint}</span>}
      </div>

      {query.isPending ? (
        <div className="mt-4 animate-pulse space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-10 rounded bg-slate-100 dark:bg-slate-800" />
          ))}
        </div>
      ) : query.isError || !d ? (
        <p className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300">
          Não foi possível gerar o relatório.
        </p>
      ) : d.rows.length === 0 ? (
        <p className="mt-4 rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
          Nenhum lançamento de {type} no período.
        </p>
      ) : (
        <>
          {/* ---------- Total e comparação ---------- */}
          <div className="mt-4 flex flex-wrap items-baseline gap-x-5 gap-y-1 rounded-xl border border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400">Total no período</p>
              <p
                className={`text-xl font-semibold ${
                  type === 'receita'
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-rose-600 dark:text-rose-400'
                }`}
              >
                {formatMoney(d.total)}
              </p>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400">
                Período anterior · {formatDate(d.previous_period.from)} a{' '}
                {formatDate(d.previous_period.to)}
              </p>
              <p className="text-sm">
                {formatMoney(d.previous_total)}{' '}
                <Variation value={d.delta_percent} type={type} />
              </p>
            </div>
          </div>

          {/* ---------- Linhas ---------- */}
          <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                <tr>
                  <th className="px-3 py-2.5 font-medium">{d.dimension_title}</th>
                  <th className="px-3 py-2.5 text-right font-medium">Valor</th>
                  <th className="px-3 py-2.5 text-right font-medium">%</th>
                  <th className="px-3 py-2.5 text-right font-medium">Anterior</th>
                  <th className="px-3 py-2.5 text-right font-medium">Variação</th>
                  <th className="px-3 py-2.5 text-right font-medium">Em aberto</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {d.rows.map((row) => (
                  <tr key={row.id ?? 'null'} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="px-3 py-2.5">
                      <span className={row.id === null ? 'text-amber-700 dark:text-amber-400' : ''}>
                        {row.label}
                      </span>
                      <span className="ml-1.5 text-xs text-slate-400">
                        {row.count} lanç.
                      </span>

                      {/* Barra de participação: o olho lê proporção antes de
                          ler número. */}
                      <div className="mt-1 h-1 w-full max-w-xs overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                        <div
                          className={`h-full rounded-full ${
                            type === 'receita' ? 'bg-emerald-500' : 'bg-rose-500'
                          }`}
                          style={{ width: `${Math.min(row.percent, 100)}%` }}
                        />
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right font-medium tabular-nums">
                      {formatMoney(row.total)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-slate-500 dark:text-slate-400">
                      {row.percent}%
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-slate-500 dark:text-slate-400">
                      {formatMoney(row.previous)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right">
                      <Variation value={row.delta_percent} type={type} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-slate-500 dark:text-slate-400">
                      {row.open === '0.0000' ? '—' : formatMoney(row.open)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Variação percentual.
 *
 * A COR depende do tipo: despesa subindo é ruim, receita subindo é boa. Pintar
 * tudo de vermelho quando sobe faria "receita +30%" parecer um alerta.
 *
 * `null` vira "novo" — crescer de zero não é 100%, é crescimento sem base.
 */
function Variation({ value, type }: { value: number | null; type: 'receita' | 'despesa' }) {
  if (value === null) {
    return <span className="text-xs text-slate-400">novo</span>;
  }

  if (value === 0) {
    return <span className="text-xs text-slate-400">—</span>;
  }

  const subiu = value > 0;
  const bom = type === 'receita' ? subiu : !subiu;

  return (
    <span
      className={`text-xs font-medium tabular-nums ${
        bom ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
      }`}
    >
      {subiu ? '▲' : '▼'} {Math.abs(value)}%
    </span>
  );
}
